/// <reference path="../../../../node_modules/monaco-editor/monaco.d.ts" />
import { Component, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { BaseComponent } from "../../shared/base.component";
import { ApiService } from "src/app/services";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalService } from "src/app/shared/modal.service";
import { CellClickedEvent, ColDef, GridApi, GridOptions, ICellRendererParams, GridReadyEvent } from "ag-grid-community";
import { PlanFile } from "src/app/generated-models/PlanFile";
import { extendDefaultOptions } from "src/app/utility/gridHelper";
import { EditDeleteCellComponent, EditDeleteCellComponentParams } from "src/app/shared/grid/edit-delete-cell.component";
import { PreprocessDateTime } from "src/app/utility/view-field-mapping";
import { PlanEditorModalOptions, PlanJsonSchema } from "./modals/plan-editor-modal/plan-editor-modal.component";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ViewCellComponent, ViewCellComponentParams } from "src/app/shared/grid/view-cell.component";
import { IsServiceUser } from "src/app/utility/userprofile-helper";
import { PlanCommit } from "src/app/generated-models/PlanCommit";
import { PlanFileTypeEnum } from "src/app/shared/enums/plan-enums";
import { PlanNewFileModalOptions } from "./modals/plan-new-file-modal/plan-new-file-modal.component";
import { ActionCellComponent, ActionCellComponentParams } from "src/app/shared/grid/action-cell.component";
import { PlanDiffModalOptions } from "./modals/plan-diff-modal/plan-diff-modal.component";
import { set } from "lodash-es";
import { ChangePlanKind } from "src/app/generated-models/ChangePlanKind";
import { PlanKindEnum } from "src/app/shared/enums/plan-enums";

@Component({
  standalone: false,
  templateUrl: "./plan-editor.component.html",
  styleUrls: ["../../styles.scss", "./plan.scss"],
})
export class PlanEditorComponent extends BaseComponent implements OnInit {
  editorModal: NgbModalRef;
  serviceTreeId: string;
  planName: string;
  serviceTeam: string;
  latestVersion: number;
  allPlanFiles: PlanFile[] = [];
  rowSourceData: PlanFile[] = [];
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize: number = 15;
  isReadonly: boolean;
  ifFilesChanged: boolean = false;
  canUndoOnboarding = false;
  canCopyToV3 = false;
  isAdmin = false;
  isOperator = false;

  get isAdminOrOperator(): boolean {
    return this.isAdmin || this.isOperator;
  }
  currentKind: string | null = null;
  rowMergedData: PlanFile[] = [];
  gridOptionsMerged: GridOptions;
  gridApiMerged: GridApi;
  originalPlanFile: PlanFile | null = null;
  hasSaveError: boolean = false;
  fileNameToOpen: string = null;

  message: string;

  jsonSchema: PlanJsonSchema = { FullSchema: "", FullSchemaV5: "", MergeSchema: "", MergeSchemaV5: "" };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private apiService: ApiService,
    protected modalService: ModalService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private title: Title
  ) {
    super();
  }

  async ngOnInit() {
    this.serviceTreeId = this.route.snapshot.params["serviceTreeId"];
    this.planName = this.route.snapshot.params["name"];
    this.fileNameToOpen = this.route.snapshot.params["fileName"];

    this.apiService
      .getUserProfile()
      .then((userProfile) => {
        if (userProfile) {
          this.isReadonly = !IsServiceUser(userProfile, this.serviceTreeId);
          if (this.isReadonly) {
            const viewCellComponentParams: ViewCellComponentParams<PlanFile> = {
              viewFunc: this.viewPlanFile.bind(this),
            };
            const columnDefs = [];
            this.gridOptions.columnDefs.map((map) => {
              if (map.headerName === "Action") {
                columnDefs.push({
                  headerName: map.headerName,
                  sortable: false,
                  suppressMenu: true,
                  cellRenderer: ViewCellComponent,
                  cellRendererParams: viewCellComponentParams,
                });
              } else {
                columnDefs.push(map);
              }
            });

            this.gridApi.setGridOption('columnDefs', columnDefs);
          }
          // If user is admin, then undo onboarding button is visible.
          this.isAdmin = !!userProfile.IsAdmin;
          this.isOperator = !!userProfile.IsOperator;
          if (userProfile.IsAdmin) {
            this.canUndoOnboarding = true;
            this.canCopyToV3 = true;
          }
        } else {
          this.notificationService.error("user profile returns null, no data was loaded.");
        }
      })
      .catch((error) => {
        this.notificationService.error(`unable to get user profile, error: ${error.message}`);
      });

    this.apiService.getPlan(this.serviceTreeId, this.planName).subscribe(
      (response) => {
        this.latestVersion = response.Version;
        this.serviceTeam = response.ServiceTeam;
        this.currentKind = response.Kind ?? null;
        var title = `${this.route.snapshot.data.title} - ${this.serviceTeam}`;
        if (this.planName) {
          title = `${this.route.snapshot.data.title} - ${this.serviceTeam} - ${this.planName}`;
        }
        this.title.setTitle(title);
        if (response.IsMigrated) {
          this.modalService.informationModal(
            "This service blueprint has been migrated hence no further commit is allowed. Please go to the new named blueprint."
          );
        }
      },
      (e: unknown) => {
        this.notificationService.error(`Failed to find plan for ServiceTree Id ${this.serviceTreeId}. Error: ${e.toString()}`);
      }
    );

    this.apiService.getPlanJsonSchema().subscribe(
      (response) => {
        this.jsonSchema.FullSchema = response.FullSchema;
        this.jsonSchema.MergeSchema = response.MergeSchema;
        this.jsonSchema.FullSchemaV5 = response.FullSchemaV5;
        this.jsonSchema.MergeSchemaV5 = response.MergeSchemaV5;
        setTimeout(function () {
          if (!response) {
            this.notificationService.error("Editor json schema returns null, no data was loaded.");
          }
        }, 200);
      },
      (e: unknown) => {
        this.notificationService.error(`Editor json schema returns null, no data was loaded. Error: ${e.toString()}`);
      }
    );

    const planFilesColumnDefinition: ColDef[] = [
      {
        headerName: "Action",
        sortable: false,
        suppressMenu: true,
        cellRendererSelector: (params: ICellRendererParams) => {
          if (params.data?.PlanFileStatus !== "Deleted") {
            return {
              component: EditDeleteCellComponent,
              params: {
                editFunc: this.editPlanFile.bind(this),
                deleteFunc: this.deletePlanFile.bind(this),
              } as EditDeleteCellComponentParams<PlanFile>,
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Name",
        field: "FileName",
      },
      {
        headerName: "Last Modified",
        field: "LastModified",
        cellRenderer: (params) => PreprocessDateTime(params.value),
      },
      {
        headerName: "File Status",
        field: "PlanFileStatus",
        cellRenderer: ActionCellComponent,
        cellRendererParams: {
          actionFunc: this.diffPlanFile.bind(this),
        } as ActionCellComponentParams<PlanFile>,
      },
      {
        headerName: "Last Modified By",
        field: "LastModifiedBy",
      },
    ];

    const planFilesMergedColumnDefinition: ColDef[] = [
      {
        headerName: "Action",
        colId: "action",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        cellRendererSelector: (params: ICellRendererParams) => {
          if (params.data?.PlanFileStatus !== "Deleted") {
            return {
              component: () => `
                <button type="button" class="btn btn-link p-0 d-inline-flex align-items-center justify-content-center" style="line-height: 1;">
                  <i class="fas fa-sm fa-code" style="margin-right: 0;"></i>
                </button>`,
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Name",
        field: "FileName",
      },
      {
        headerName: "Last Modified",
        field: "LastModified",
        cellRenderer: (params) => PreprocessDateTime(params.value),
      },
      {
        headerName: "File Status",
        field: "PlanFileStatus",
        cellRenderer: ActionCellComponent,
        cellRendererParams: {
          actionFunc: this.diffPlanFile.bind(this),
        } as ActionCellComponentParams<PlanFile>,
      },
      {
        headerName: "File Type",
        field: "PlanFileType",
      },
    ];

    this.gridOptions = extendDefaultOptions({
      columnDefs: planFilesColumnDefinition,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      sideBar: null,
      rowClassRules: {
        "ag-row-added": (params) => {
          return params.data.PlanFileStatus === "Added";
        },
        "ag-row-modified": (params) => {
          return params.data.PlanFileStatus === "Modified";
        },
        "ag-row-deleted": (params) => {
          return params.data.PlanFileStatus === "Deleted";
        },
      },
    });

    this.gridOptionsMerged = extendDefaultOptions({
      onGridReady: (params: GridReadyEvent) => { this.gridApiMerged = params.api; },
      onCellClicked: this.onCellClicked.bind(this),
      columnDefs: planFilesMergedColumnDefinition,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      sideBar: null,
      rowClassRules: {
        "ag-row-added": (params) => {
          return params.data.PlanFileStatus === "Added";
        },
        "ag-row-modified": (params) => {
          return params.data.PlanFileStatus === "Modified";
        },
        "ag-row-deleted": (params) => {
          return params.data.PlanFileStatus === "Deleted";
        },
      },
    });

    await this.getPlanFiles();

    // Auto-open modal if fileName parameter is present (for direct URL access)
    if (this.fileNameToOpen) {
      await this.autoOpenFileModal(this.fileNameToOpen);
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  protected async onCellClicked(event: CellClickedEvent) {
    if (event.colDef.colId === "action") {
      this.viewPlanFile(event.data as PlanFile);
    }
  }

  async getPlanFiles() {
    this.loadingService.setLoading(true);

    try {
      const response = await this.apiService.getLatestPlanFiles(this.serviceTreeId, this.planName).toPromise();
      const original: PlanFile[] = [];
      const generated: PlanFile[] = [];
      response.forEach((plan) => {
        if (plan.FileName === "default.json") {
          original.push(plan);
          generated.push(plan);
        } else if (plan.FileName.includes("override")) {
          original.push(plan);
        } else {
          if (plan.PlanFileType === PlanFileTypeEnum.Generated) {
            generated.push(plan);
          } else {
            original.push(plan);
            generated.push(plan);
          }
        }
      });
      this.rowSourceData = original;
      this.rowMergedData = generated;
      this.allPlanFiles = response;
      this.ifFilesChanged = this.rowSourceData.filter((row) => row.PlanFileStatus != "NoChange").length > 0;
      this.loadingService.setLoading(false);
      setTimeout(() => {
        this.gridApi.autoSizeAllColumns();
        this.gridApiMerged.autoSizeAllColumns();
      }, 100);
    } catch (e: unknown) {
      this.rowSourceData = [];
      this.rowMergedData = [];
      this.allPlanFiles = [];
      this.loadingService.setLoading(false);
      this.notificationService.error(e.toString());
    }
  }

  async viewPlanFile(planFile: PlanFile) {
    await this.showPlanEditorDialog(planFile, true);
  }

  async editPlanFile(planFile: PlanFile) {
    // If there was a save error previously, reload the original plan file for editing
    if (this.hasSaveError && this.originalPlanFile) {
      await this.showPlanEditorDialog(this.originalPlanFile, false);
    } else {
      await this.showPlanEditorDialog(planFile);
    }
  }

  async diffPlanFile(planFile: PlanFile) {
    await this.showPlanDiffDialog(planFile);
  }

  async deletePlanFile(planFile: PlanFile) {
    try {
      await this.modalService.confirmationModal(`Are you sure to delete ${planFile.FileName}?`);
    } catch {
      // For the model dialog dismiss
      return;
    }

    this.loadingService.setLoading(true);

    this.apiService.deletePlanFile(this.serviceTreeId, planFile.FileName, this.planName).subscribe(
      () => {
        this.message = `Plan file has been deleted successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      },
      () => {
        this.getPlanFiles();
      }
    );
  }

  async showPlanNewFileDialog() {
    const cloudOverrideExist = this.rowSourceData.some((file) => file.FileName.includes("cloud"));
    const regionTypeOverrideExist = this.rowSourceData.some((file) => file.FileName.includes("type"));
    const overrideNameList = this.rowSourceData.map((file) => file.FileName.split(".")[0]);
    const options: PlanNewFileModalOptions = {
      cloudOverrideExist: cloudOverrideExist,
      regionTypeOverrideExist: regionTypeOverrideExist,
      overrideNameList: overrideNameList,
    };
    const fileName: string = await this.modalService.planNewFileModal(options);

    if (fileName) {
      const planFile: PlanFile = this.rowSourceData.find((p) => p.FileName === `${fileName}.json`);
      if (planFile) {
        await this.showPlanEditorDialog(planFile);
      } else {
        const emptyPlanFile = {
          FileName: `${fileName}.json`,
          Content: "{}",
          Format: "json",
        } as PlanFile;

        if (fileName.includes("override")) {
          // partial mode
          const cloudOrRegionName = fileName.split(".")[0];
          await this.checkOverrideExist(cloudOrRegionName, emptyPlanFile);
        } else {
          // fully mode
          const planTemplate = await this.apiService.getPlanFileTemplate(this.serviceTreeId, this.planName).toPromise();
          const cloudOrRegionName = fileName;
          emptyPlanFile.Content = planTemplate.Content;
          await this.checkOverrideExist(cloudOrRegionName, emptyPlanFile);
        }
      }
    }
  }

  async checkOverrideExist(cloudOrRegionName: string, emptyPlanFile: PlanFile) {
    const planFile: PlanFile = this.allPlanFiles.find((p) => p.FileName === `${cloudOrRegionName}.json`);
    if (planFile) {
      this.notificationService.error(`An override blueprint file for ${cloudOrRegionName} alread exists.`);
    } else {
      await this.showPlanEditorDialog(emptyPlanFile);
    }
  }

  async showPlanEditorDialog(planFile: PlanFile, isReadonly: boolean = false) {

    // Save original Plan file content when editor opens,Use JSON deep copy to avoid mutation
    this.originalPlanFile = JSON.parse(JSON.stringify(planFile));

    const schema = planFile.FileName.includes("override") ? this.jsonSchema.MergeSchema : this.jsonSchema.FullSchema;
    const options: PlanEditorModalOptions = {
      model: planFile,
      save: this.savePlanFile.bind(this),
      isReadonly: isReadonly,
      schema: schema,
      serviceTreeId: this.serviceTreeId,
      planName: this.planName,
    };
    this.editorModal = await this.modalService.planEditorModal(options);
  }

  async showPlanDiffDialog(planFile: PlanFile) {
    const options: PlanDiffModalOptions = {
      model: planFile,
      isReadonly: true,
    };
    this.editorModal = await this.modalService.planDiffModal(options);
  }

  savePlanFile(planFile: PlanFile) {
    this.loadingService.setLoading(true);

    this.apiService.savePlanFile(this.serviceTreeId, planFile, this.planName).subscribe(
      () => {
        this.message = `Plan file has been saved successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
        this.editorModal.close();
        this.hasSaveError = false;
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
        this.hasSaveError = true;
      },
      () => {
        this.getPlanFiles();
      }
    );
  }

  async undoOnboarding() {
    try {
      await this.modalService.confirmationModal(
        `Are you sure to undo onboarding for service ${this.serviceTreeId}? This will delete all blueprint files and service owner need to re-onboard again.`
      );
    } catch {
      // For the model dialog dismiss
      return;
    }

    this.loadingService.setLoading(true);

    this.apiService.undoOnboardPlan(this.serviceTreeId, this.planName).subscribe(
      () => {
        this.message = `All blueprint files for service ${this.serviceTreeId} are removed.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);

        // delay and redirect to plan list page
        setTimeout(() => {
          this.router.navigateByUrl("/plans/all");
        }, 1000);
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }

  async copyToPlanV3() {
    try {
      await this.modalService.confirmationModal(
        `Are you sure to copy all versions to PlanV3 for service ${this.serviceTreeId} and blueprint ${this.planName}?`
      );
    } catch {
      // For the model dialog dismiss
      return;
    }

    this.loadingService.setLoading(true);

    this.apiService.copyToPlanV3(this.serviceTreeId, this.planName).subscribe(
      () => {
        this.message = `Copy all versions to PlanV3 successful for service ${this.serviceTreeId} and blueprint ${this.planName}.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }

  async showPlanHistoryDialog() {
    await this.modalService.planHistoryModal(this.serviceTreeId, this.planName);
  }

  async changeKind() {
    if (!this.isAdminOrOperator) {
      await this.modalService.informationModal(
        "Only administrators or operators can change kind. Please post to https://aka.ms/lionrockhelp to get help.",
        "Change Kind"
      );
      return;
    }

    let newKind: PlanKindEnum;
    try {
      newKind = (await this.modalService.planChangeKindModal({
        serviceTreeId: this.serviceTreeId,
        planName: this.planName,
        currentKind: this.currentKind,
      })) as PlanKindEnum;
    } catch {
      // For the modal dialog dismiss
      return;
    }

    if (!newKind) {
      return;
    }

    this.loadingService.setLoading(true);

    const payload: ChangePlanKind = { PlanKind: newKind };
    this.apiService.changePlanKind(this.serviceTreeId, this.planName, payload).subscribe(
      () => {
        this.message = `Blueprint kind for ${this.planName} (service ${this.serviceTreeId}) has been changed to ${newKind}.`;
        this.notificationService.info(this.message);
        this.currentKind = newKind;
        this.loadingService.setLoading(false);
      },
      (error) => {
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }

  async showCommitPlanDialog() {
    try {
      const comment = await this.modalService.planCommitModal();
      await this.commitPlan(comment);
    } catch {
      // For the model dialog dismiss
    }
  }

  async commitPlan(comment: string) {
    this.loadingService.setLoading(true);

    const payload: PlanCommit = {
      Comment: comment,
    };

    this.apiService.commitPlan(this.serviceTreeId, payload, this.planName).subscribe(
      async (commitPlanResult) => {
        this.message = `Successfully committed the plan version(<b>${commitPlanResult.Version}</b>).</br>`;

        if (commitPlanResult.AutoAssignedRegions != null) {
          let regions = commitPlanResult.AutoAssignedRegions.join(", ");
          this.message += `Auto assigned to <b>{${regions}}</b> successfully.</br>`;
        }

        if (commitPlanResult.ValidateFailedRegionsOfCrp != null) {
          for (let region in commitPlanResult.ValidateFailedRegionsOfCrp) {
            let familyOrSizes = commitPlanResult.ValidateFailedRegionsOfCrp[region].join(", ");
            this.message += `Skip auto assigning to <b>{${region}}</b> because VM SKUs <b>{${familyOrSizes}}</b> may not be available in the region. You can try assigning the plan manually.</br>`;
          }
        }

        if (commitPlanResult.RegionsFailedToAssign != null) {
          for (let region in commitPlanResult.RegionsFailedToAssign) {
            let errorMessage = commitPlanResult.RegionsFailedToAssign[region];
            this.message += `Failed to assign to <b>{${region}}</b>. Error: {${errorMessage}} .</br>`;
          }
        }

        if (commitPlanResult.ValidateFailedRegionsOfNamespaces != null) {
          for (let region in commitPlanResult.ValidateFailedRegionsOfNamespaces) {
            let namespaces = commitPlanResult.ValidateFailedRegionsOfNamespaces[region].join(", ");
            this.message += `Skip auto assigning to <b>{${region}}</b> because namespace <b>{${namespaces}}</b> may not be available in the region. You can try assigning the plan manually.</br>`;
          }
        }

        if (commitPlanResult.RegionsWithVmSkuPslWarnings != null) {
          for (let region in commitPlanResult.RegionsWithVmSkuPslWarnings) {
            let vmSkus = commitPlanResult.RegionsWithVmSkuPslWarnings[region].join(", ");
            this.message += `PSL warning for <b>{${region}}</b>: VM SKUs <b>{${vmSkus}}</b> (or their alternatives) are not found in the regional PSL. Please review alternatives for this region before manual assignment.</br>`;
          }
        }

        this.loadingService.setLoading(false);
        await this.modalService.planCommitResponseModal(this.message);

        this.getPlanFiles();
        this.latestVersion = commitPlanResult.Version;
      },
      (error) => {
        if (!error) {
          error = `Failed to commit plan.`;
        }
        this.notificationService.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }

  async autoOpenFileModal(fileNameParam: string) {
    const targetFileName = fileNameParam.endsWith(".json") ? fileNameParam : `${fileNameParam}.json`;
    const planFile = this.allPlanFiles.find((p) => p.FileName === targetFileName);

    if (planFile) {
      // Always open in read-only mode for direct URL access
      await this.showPlanEditorDialog(planFile, true);

      // Listen for modal close and redirect back to editor
      this.editorModal.result.then(
        () => {
          // Modal closed - redirect to editor without fileName parameter
          this.router.navigate(["/plans/services", this.serviceTreeId, this.planName, "editor"]);
        },
        () => {
          // Modal dismissed - redirect to editor without fileName parameter
          this.router.navigate(["/plans/services", this.serviceTreeId, this.planName, "editor"]);
        }
      );
    } else {
      this.notificationService.error(`Plan file "${targetFileName}" not found.`);
    }
  }
}
